import { create } from 'zustand';
import type { AdminRemoteAction } from '@livepuls/types';
import { getSocket } from '../lib/socket.js';

/**
 * Realtime-Status-Spiegel für alle Panels:
 *  - `online`: Set<panelId> aus presence:update
 *  - `pressed`: Map<panelId, Set<buttonId>> aus panel:state
 *
 * Wird ausschließlich für UI-Visualisierung verwendet (Remote-Mirror,
 * Online-Dots). Persistenz erfolgt nicht — beim Reload neu vom Backend.
 */

type PressedMap = Record<string, ReadonlySet<string>>;

interface LiveStateData {
  online: ReadonlySet<string>;
  pressed: PressedMap;
}

interface LiveState extends LiveStateData {
  bind(): () => void;
  sendAction(panelId: string, action: AdminRemoteAction): void;
  togglePress(panelId: string, buttonId: string): void;
  isPressed(panelId: string, buttonId: string): boolean;
}

export const useLiveStateStore = create<LiveState>((set, get) => ({
  online: new Set<string>(),
  pressed: {},

  bind() {
    const socket = getSocket();

    const onPresence = (e: { panelId: string; online: boolean }): void => {
      set((s) => {
        const next = new Set(s.online);
        if (e.online) next.add(e.panelId);
        else next.delete(e.panelId);
        return { online: next };
      });
    };

    const onState = (e: { panelId: string; buttonId: string; active: boolean }): void => {
      set((s) => {
        const current = s.pressed[e.panelId] ?? new Set<string>();
        const next = new Set(current);
        if (e.active) next.add(e.buttonId);
        else next.delete(e.buttonId);
        return { pressed: { ...s.pressed, [e.panelId]: next } };
      });
    };

    socket.on('presence:update', onPresence);
    socket.on('panel:state', onState);

    return () => {
      socket.off('presence:update', onPresence);
      socket.off('panel:state', onState);
    };
  },

  sendAction(panelId, action) {
    getSocket().emit('admin:remote-action', {
      panelId,
      action,
      at: new Date().toISOString(),
    });
  },

  togglePress(panelId, buttonId) {
    const pressedNow = get().isPressed(panelId, buttonId);
    get().sendAction(panelId, {
      kind: pressedNow ? 'button-release' : 'button-press',
      buttonId,
    });
    // Optimistic local update — wird bei Server-Echo bestätigt.
    set((s) => {
      const current = s.pressed[panelId] ?? new Set<string>();
      const next = new Set(current);
      if (pressedNow) next.delete(buttonId);
      else next.add(buttonId);
      return { pressed: { ...s.pressed, [panelId]: next } };
    });
  },

  isPressed(panelId, buttonId) {
    return get().pressed[panelId]?.has(buttonId) ?? false;
  },
}));
